import { format as dateFnsFormat, isValid, startOfYear } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import parseValueToDate from './helpers/parse-value-to-date';
import InputWrapper from './input-wrapper';
import MonthPicker from './month-picker';

const MonthPickerInput = React.forwardRef((props, ref) => {
  const {
    defaultValue,
    value: valueProp,
    format,
    onChange,
    InputProps,
    MonthPickerProps,
    ...otherProps
  } = props;
  const [isDayPickerDisplayed, setIsDayPickerDisplayed] = useState(false);
  const isControlled = useRef(valueProp !== undefined && valueProp !== null);
  const defaultDate = useMemo(() => parseValueToDate(defaultValue, format), [defaultValue, format]);

  const [internalSelectedDays, setInternalSelectedDays] = useState(
    isValid(defaultDate) ? defaultDate : null
  );
  const [internalValue, setInternalValue] = useState(defaultValue);

  const inputValue = isControlled.current ? valueProp : internalValue;
  const selectedDays = isControlled.current
    ? parseValueToDate(valueProp, format)
    : internalSelectedDays;

  const [month, setMonth] = useState(isValid(selectedDays) ? selectedDays : new Date());

  const handleMonthChange = (date) => {
    setMonth(date);
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    const date = parseValueToDate(value, format);
    if (!isControlled.current) {
      setInternalValue(value);
      setInternalSelectedDays(date);
    }
    if (isValid(date)) {
      setMonth(date);
    }
    if (onChange) onChange(e, date, value);
  };

  const handleClickCaption = (date, e) => {
    const value = dateFnsFormat(date, format);
    if (!isControlled.current) {
      setInternalSelectedDays(date);
      setInternalValue(value);
    }
    if (onChange) onChange(e, date, value);
    setIsDayPickerDisplayed(false);
  };

  return (
    <InputWrapper
      ref={ref}
      open={isDayPickerDisplayed}
      onClose={() => {
        setIsDayPickerDisplayed(false);
      }}
      onOpen={() => {
        setIsDayPickerDisplayed(true);
      }}
      InputProps={{ value: inputValue, onChange: handleInputChange, ...InputProps }}
      datePicker={
        <MonthPicker
          selectedDays={[selectedDays]}
          onCaptionClick={handleClickCaption}
          onMonthChange={handleMonthChange}
          month={startOfYear(month)}
          {...MonthPickerProps}
          className={MonthPickerProps.className}
        />
      }
      {...otherProps}
    />
  );
});

MonthPickerInput.propTypes = {
  // Input's initial value
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  // used to format selected Date to string
  // or parse input string to Date
  format: PropTypes.string,
  // will return a date after input being changed or a month being selected
  onChange: PropTypes.func,
  // props applied to Input
  InputProps: PropTypes.object,
  TooltipProps: PropTypes.object,
  MonthPickerProps: PropTypes.object,
};

MonthPickerInput.defaultProps = {
  defaultValue: undefined,
  value: undefined,
  onChange: undefined,
  format: 'yyyy-MM',
  InputProps: {},
  TooltipProps: {},
  MonthPickerProps: {},
};

export default MonthPickerInput;
